import moment, { Moment } from 'moment';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../store/store';
import { useEffect, useRef, useState } from 'react';
import RegenerateModal from '../../RegenerateModal';
import CustomModal from '../../CustomModal';
import useWindowWidth from '../../../../hooks/useWindowWidth';
import { useUserExamUpdate } from '../../../../utils/updateUserExam';
import { examsSelectors } from '../../../../slices/exams/examsSlice';
import CustomExamDatePicker from '../../CustomExamDatePicker';

interface ExamDateSelectProps {
  setIsLoadingStudySessionAndContent: (isLoading: boolean) => void;
  setIsLoadedStudySessionAndContent: (isLoaded: boolean) => void;
  refreshStudySessionAndContent: () => void;
  disabled?: boolean;
}

const ExamDateSelect: React.FC<ExamDateSelectProps> = ({
  setIsLoadingStudySessionAndContent,
  setIsLoadedStudySessionAndContent,
  refreshStudySessionAndContent,
  disabled = false,
}) => {
  const currentUser = useSelector((state: RootState) => state.user.profile);
  const todaySession = useSelector((state: RootState) => state.todaySession);
  const primaryExam = useSelector(examsSelectors.selectPrimaryExam);

  const [tempExamDate, setTempExamDate] = useState<Moment | null>(
    primaryExam?.examDate ? moment.utc(primaryExam.examDate) : null
  );

  const [examDateSelectionModalIsOpen, setExamDateSelectionModalIsOpen] =
    useState(false);
  const [showRegenModal, setShowRegenModal] = useState(false);
  const [showSaveConfirmation, setShowSaveConfirmation] = useState(false);
  const [unsavedChanges, setUnsavedChanges] = useState(false);

  const windowWidth = useWindowWidth();

  const cancelButtonRef = useRef<HTMLButtonElement>(null);
  const submitButtonRef = useRef<HTMLButtonElement>(null);

  const { updateExam } = useUserExamUpdate(refreshStudySessionAndContent);

  useEffect(() => {
    if (primaryExam) {
      const primaryExamDate = primaryExam.examDate
        ? moment.utc(primaryExam.examDate)
        : null;
      const isSameDate =
        (primaryExamDate === null && tempExamDate === null) ||
        (primaryExamDate &&
          tempExamDate &&
          primaryExamDate.format('YYYY-MM-DD') ===
            tempExamDate.utc().format('YYYY-MM-DD'));
      setUnsavedChanges(!isSameDate);
    } else {
      setUnsavedChanges(false);
    }
  }, [tempExamDate, primaryExam]);

  useEffect(() => {
    if (primaryExam) {
      const examMoment = primaryExam.examDate
        ? moment.utc(primaryExam.examDate)
        : null;
      setTempExamDate(examMoment);
    }
  }, [primaryExam, examDateSelectionModalIsOpen]);

  const saveChangesOrShowConfirmation = async () => {
    if (todaySession.isStarted === false) {
      submitChanges(true);
    } else if (todaySession.isComplete) {
      submitChanges(false);
    } else {
      setShowRegenModal(true);
    }
  };

  const handleDateChange = async (date: Moment | null, regenerate: boolean) => {
    if (!currentUser || !primaryExam || !primaryExam.id) return;
    await updateExam({
      userId: currentUser.id,
      examId: primaryExam.id,
      examDate: date ? date.utc().format('YYYY-MM-DDTHH:mm:ss[Z]') : null,
      hasCompletedRealExam: false,
      clearTodaySession: regenerate,
    });

    if (document.activeElement && document.activeElement !== document.body) {
      (document.activeElement as HTMLElement).blur();
    }
  };

  const submitChanges = async (regenerate: boolean) => {
    setExamDateSelectionModalIsOpen(false);
    setIsLoadingStudySessionAndContent(true);
    setIsLoadedStudySessionAndContent(false);
    if (showSaveConfirmation) {
      setShowSaveConfirmation(false);
      setUnsavedChanges(false);
    }
    await handleDateChange(tempExamDate, regenerate);
    setIsLoadingStudySessionAndContent(false);
    setIsLoadedStudySessionAndContent(true);
  };

  const handleUnsavedChanges = () => {
    if (cancelButtonRef.current) {
      cancelButtonRef.current.classList.add('shake');
    }
    if (submitButtonRef.current) {
      submitButtonRef.current.classList.add('shake');
    }
    setTimeout(() => {
      if (cancelButtonRef.current) {
        cancelButtonRef.current.classList.remove('shake');
      }
      if (submitButtonRef.current) {
        submitButtonRef.current.classList.remove('shake');
      }
    }, 500);
  };

  return (
    <>
      <button
        className="button button--glass button--exam-date-selection"
        onClick={() => {
          if (disabled) return;
          setExamDateSelectionModalIsOpen(true);
        }}
        tabIndex={0}
        disabled={disabled}
      >
        <div className="button--exam-date-selection_label">Exam Date</div>
        <div className="button--exam-date-selection_value">
          {primaryExam?.examDate
            ? moment
                .utc(primaryExam.examDate)
                .format(windowWidth < 900 ? 'MMM. D, YYYY' : 'MMMM D, YYYY')
            : 'No Date Selected'}
        </div>
      </button>
      <CustomModal
        open={examDateSelectionModalIsOpen}
        onClose={() => setExamDateSelectionModalIsOpen(false)}
        name={'exam-date-selection'}
        glass={true}
        unsavedChanges={unsavedChanges}
        handleUnsavedChanges={handleUnsavedChanges}
      >
        <div className="modal_header">
          <div className="exam-title">
            <span>{primaryExam?.name}</span>
            <h2>Exam Date</h2>
          </div>
        </div>
        <div className="modal_content">
          <CustomExamDatePicker
            value={tempExamDate ? tempExamDate.utc() : null}
            onChange={(newValue) => {
              if (disabled) return;
              setTempExamDate(newValue?.utc() || null);
            }}
            disabled={disabled}
          />
        </div>
        <div className="modal_actions">
          <button
            ref={cancelButtonRef}
            className="button button--glass"
            onClick={() => {
              setExamDateSelectionModalIsOpen(false);
              if (primaryExam?.examDate) {
                setTempExamDate(moment.utc(primaryExam.examDate));
              } else {
                setTempExamDate(null);
              }
            }}
          >
            Cancel
          </button>
          <button
            ref={submitButtonRef}
            className="button button--glass"
            onClick={saveChangesOrShowConfirmation}
            disabled={disabled || !unsavedChanges}
          >
            Confirm
          </button>
        </div>
      </CustomModal>
      <RegenerateModal
        showRegenModal={showRegenModal}
        setShowRegenModal={setShowRegenModal}
        submitChanges={submitChanges}
        type={'exam-settings'}
      />
    </>
  );
};

export default ExamDateSelect;
